import { useState, useMemo, useCallback } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { MilestoneForm } from '@/components/Calendars/MilestoneForm'
import { MilestoneOverviewList } from '@/components/Calendars/MilestoneOverviewList'
import type { CalendarEventRead } from '@/types'

interface MilestoneEditorPanelProps {
  calendarId: string
  event: CalendarEventRead
  allEvents: CalendarEventRead[]
  onClose: () => void
}

export function MilestoneEditorPanel({
  calendarId,
  event,
  allEvents,
  onClose,
}: MilestoneEditorPanelProps) {
  const [selectedMilestoneId, setSelectedMilestoneId] = useState<string | null>(
    () => {
      const sorted = [...event.milestones].sort(
        (a, b) => a.display_order - b.display_order
      )
      return sorted.length > 0 ? sorted[0].id : null
    }
  )
  const [isCreating, setIsCreating] = useState(false)

  const allMilestones = useMemo(
    () => allEvents.flatMap(e => e.milestones),
    [allEvents]
  )

  const sortedMilestones = useMemo(
    () =>
      [...event.milestones].sort((a, b) => a.display_order - b.display_order),
    [event.milestones]
  )

  const selectedMilestone = useMemo(() => {
    if (isCreating) return null
    if (!selectedMilestoneId) return null
    return event.milestones.find(m => m.id === selectedMilestoneId) ?? null
  }, [selectedMilestoneId, event.milestones, isCreating])

  const handleSelect = useCallback((id: string) => {
    setIsCreating(false)
    setSelectedMilestoneId(id)
  }, [])

  const handleAddNew = useCallback(() => {
    setIsCreating(true)
    setSelectedMilestoneId(null)
  }, [])

  const handleSaved = useCallback(() => {
    setIsCreating(false)
  }, [])

  const handleCancel = useCallback(() => {
    setIsCreating(false)
    // Re-select the first milestone if we were creating
    if (!selectedMilestoneId && sortedMilestones.length > 0) {
      setSelectedMilestoneId(sortedMilestones[0].id)
    }
  }, [selectedMilestoneId, sortedMilestones])

  return (
    <Sheet open onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="sm:max-w-[900px] w-full overflow-hidden"
      >
        <SheetHeader className="shrink-0">
          <SheetTitle>{event.name}</SheetTitle>
          <SheetDescription>Manage milestones for this event</SheetDescription>
        </SheetHeader>

        <div className="grid flex-1 min-h-0 grid-cols-[260px_1fr]">
          {/* LEFT: Milestones overview list */}
          <div className="flex flex-col overflow-hidden border-r px-4 pb-4">
            <h3 className="shrink-0 mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Milestones
            </h3>
            <div className="flex-1 min-h-0">
              <MilestoneOverviewList
                milestones={sortedMilestones}
                allMilestones={allMilestones}
                selectedId={isCreating ? null : selectedMilestoneId}
                onSelect={handleSelect}
                calendarId={calendarId}
                eventId={event.id}
                onAddNew={handleAddNew}
              />
            </div>
          </div>

          {/* RIGHT: Milestone editor form */}
          <div className="overflow-y-auto px-6 pb-6">
            {isCreating || selectedMilestone ? (
              <>
                <h3 className="mb-4 text-base font-semibold text-foreground">
                  {isCreating ? 'New Milestone' : selectedMilestone?.name}
                </h3>
                <MilestoneForm
                  calendarId={calendarId}
                  eventId={event.id}
                  milestone={isCreating ? null : selectedMilestone}
                  siblingMilestones={sortedMilestones}
                  allMilestones={allMilestones}
                  onSaved={handleSaved}
                  onCancel={handleCancel}
                />
              </>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Select a milestone to edit, or add a new one.
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
